import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import type { CloseDialogHandler } from "../../contexts/DialogContext";
import { useCreateKpi } from "../../hooks/mutation/create/useCreateKpi";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  description: z.string().optional().default(""),
  warning: z.number().min(0, "Minimum is 0").max(100, "Maximum is 100"),
  critical: z.number().min(0, "Minimum is 0").max(100, "Maximum is 100"),
});

type FormValues = z.input<typeof schema>;

const NewKpiDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
}> = ({ onDialogClosed }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const { mutateAsync: createKpi, isPending: isCreating } = useCreateKpi();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      warning: 50,
      critical: 50,
    },
    mode: "onBlur",
  });

  const onSubmit = async (data: FormValues) => {
    try {
      setSubmitError(null);
      await createKpi({
        name: data.name.trim(),
        description: data.description?.trim() ?? "",
        thresholds: {
          warning: data.warning,
          critical: data.critical,
        },
      });

      reset({ name: "", description: "", warning: 50, critical: 50 });
      onDialogClosed({ message: "KPI created!", severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    if (isCreating) {
      return;
    }
    setSubmitError(null);
    reset({ name: "", description: "", warning: 50, critical: 50 });
    onDialogClosed();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle
        title="New KPI"
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box component="form" id="new-kpi-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Create a KPI to use in service templates and business services.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Error Rate"
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Short description of the KPI"
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <Stack spacing={0.25}>
                <Typography variant="subtitle1">Thresholds</Typography>
                <FormHelperText sx={{ m: 0 }}>
                  Use absolute score values (0-100), not percentages. Example:
                  warning 70, critical 40.
                </FormHelperText>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Warning threshold</FormLabel>
                  <TextField
                    size="small"
                    type="number"
                    error={!!errors.warning}
                    inputProps={{ min: 0, max: 100 }}
                    {...register("warning", {
                      valueAsNumber: true,
                      onChange: () => submitError && setSubmitError(null),
                    })}
                  />
                  <FormHelperText error={!!errors.warning}>
                    {errors.warning?.message ?? " "}
                  </FormHelperText>
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Critical threshold</FormLabel>
                  <TextField
                    size="small"
                    type="number"
                    error={!!errors.critical}
                    inputProps={{ min: 0, max: 100 }}
                    {...register("critical", {
                      valueAsNumber: true,
                      onChange: () => submitError && setSubmitError(null),
                    })}
                  />
                  <FormHelperText error={!!errors.critical}>
                    {errors.critical?.message ?? " "}
                  </FormHelperText>
                </FormControl>
              </Stack>

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText="Create"
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isCreating}
          disabled={isCreating}
        />
      </Box>
    </Dialog>
  );
};

export default NewKpiDialog;
